import React from "react";
import { useTranslation } from "react-i18next";
import {
  Descriptions,
  Divider,
  Space,
  Spin,
  Empty,
  Button,
  Table,
  Tag,
  Collapse,
} from "antd";
import {
  DownloadOutlined,
  FileTextOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import type {
  LeanCustomsOrganization,
  LeanCustomsContract,
  LeanCustomsContractSigner,
  LeanCustomsContractSupportDocument,
} from "../../services/leancustomsService";

const { Panel } = Collapse;

export interface LeanCustomsIntegrationDetailsContentProps {
  organization: LeanCustomsOrganization | null;
  contracts: LeanCustomsContract[];
  loadingContracts: boolean;
  contractSigners: Record<string, LeanCustomsContractSigner[]>;
  loadingSigners: Record<string, boolean>;
  contractSupportDocuments: Record<string, LeanCustomsContractSupportDocument[]>;
  loadingSupportDocuments: Record<string, boolean>;
  onContractPanelsChange: (activeKeys: string | string[]) => void;
  onDownloadSignerDocument: (
    contractId: string,
    signerId: string,
    name: string
  ) => void;
  onDownloadSupportDocument: (
    contractId: string,
    docId: string,
    filename: string
  ) => void;
}

export const LeanCustomsIntegrationDetailsContent: React.FC<
  LeanCustomsIntegrationDetailsContentProps
> = ({
  organization,
  contracts,
  loadingContracts,
  contractSigners,
  loadingSigners,
  contractSupportDocuments,
  loadingSupportDocuments,
  onContractPanelsChange,
  onDownloadSignerDocument,
  onDownloadSupportDocument,
}) => {
  const { t } = useTranslation();

  if (!organization) {
    return (
      <Empty
        description={t("customs.noOrganizationData")}
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  return (
    <>
      <Descriptions bordered column={2} size="small">
        <Descriptions.Item label={t("customs.organizationName")} span={2}>
          {organization.name}
        </Descriptions.Item>
        <Descriptions.Item label={t("customs.country")}>
          {organization.country_code}
        </Descriptions.Item>
        <Descriptions.Item label={t("customs.eoriNumber")}>
          {organization.eori_number || "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("customs.cocNumber")}>
          {organization.coc_number || "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("customs.vatNumber")}>
          {organization.vat_number || "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("customs.address")} span={2}>
          {organization.street}, {organization.postcode} {organization.place}
        </Descriptions.Item>
      </Descriptions>

      <Divider orientation="left">
        <Space>
          <FileTextOutlined />
          <span>{t("customs.contracts")}</span>
        </Space>
      </Divider>
      {loadingContracts ? (
        <Spin />
      ) : contracts.length > 0 ? (
        <Collapse
          onChange={(keys) => {
            if (Array.isArray(keys)) {
              onContractPanelsChange(keys);
            } else if (keys != null) {
              onContractPanelsChange([keys]);
            }
          }}
        >
          {contracts.map((contract) => (
            <Panel
              key={contract.id}
              header={
                <Space>
                  <span>
                    {contract.contract_reference || contract.id.substring(0, 8)}
                  </span>
                  <Tag
                    color={
                      contract.status === "signed"
                        ? "green"
                        : contract.status === "active"
                          ? "blue"
                          : contract.status === "pending"
                            ? "orange"
                            : "default"
                    }
                  >
                    {contract.status}
                  </Tag>
                </Space>
              }
            >
              <Descriptions bordered column={2} size="small">
                <Descriptions.Item
                  label={t("customs.contractReference")}
                  span={2}
                >
                  {contract.contract_reference || "-"}
                </Descriptions.Item>
                <Descriptions.Item label={t("customs.status")}>
                  <Tag
                    color={
                      contract.status === "signed"
                        ? "green"
                        : contract.status === "active"
                          ? "blue"
                          : "default"
                    }
                  >
                    {contract.status}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label={t("customs.agent")}>
                  {contract.agent_name || "-"}
                </Descriptions.Item>
                <Descriptions.Item label={t("customs.startDate")}>
                  {contract.start_date
                    ? new Date(contract.start_date).toLocaleDateString()
                    : "-"}
                </Descriptions.Item>
                <Descriptions.Item label={t("customs.endDate")}>
                  {contract.end_date
                    ? new Date(contract.end_date).toLocaleDateString()
                    : "-"}
                </Descriptions.Item>
                <Descriptions.Item label={t("customs.signedOn")}>
                  {contract.signed_on
                    ? new Date(contract.signed_on).toLocaleDateString()
                    : "-"}
                </Descriptions.Item>
              </Descriptions>

              <div style={{ marginTop: 16 }}>
                <Divider orientation="left">
                  <Space>
                    <TeamOutlined />
                    <span>{t("customs.signers")}</span>
                  </Space>
                </Divider>
                {loadingSigners[contract.id] ? (
                  <Spin />
                ) : contractSigners[contract.id]?.length > 0 ? (
                  <Table
                    dataSource={contractSigners[contract.id]}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    columns={[
                      {
                        title: t("customs.name"),
                        dataIndex: "name",
                        key: "name",
                      },
                      {
                        title: t("customs.position"),
                        dataIndex: "position",
                        key: "position",
                        render: (pos: string) => pos || "-",
                      },
                      {
                        title: t("customs.email"),
                        dataIndex: "email",
                        key: "email",
                        render: (email: string) => email || "-",
                      },
                      {
                        title: t("customs.status"),
                        dataIndex: "status",
                        key: "status",
                        render: (status: string) => (
                          <Tag
                            color={
                              status === "signed"
                                ? "green"
                                : status === "pending"
                                  ? "orange"
                                  : "default"
                            }
                          >
                            {status || "unknown"}
                          </Tag>
                        ),
                      },
                      {
                        title: t("common.actions"),
                        key: "action",
                        render: (_: unknown, signer: LeanCustomsContractSigner) =>
                          signer.id &&
                          (signer.document || signer.document_type) ? (
                            <Button
                              type="link"
                              size="small"
                              icon={<DownloadOutlined />}
                              onClick={() =>
                                onDownloadSignerDocument(
                                  contract.id,
                                  signer.id!,
                                  signer.name
                                )
                              }
                            >
                              {t("customs.downloadDocument")}
                            </Button>
                          ) : null,
                      },
                    ]}
                  />
                ) : (
                  <Empty
                    description={t("customs.noSigners")}
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    style={{ padding: "20px 0" }}
                  />
                )}
              </div>

              <div style={{ marginTop: 16 }}>
                <Divider orientation="left">
                  <Space>
                    <DownloadOutlined />
                    <span>{t("customs.supportDocuments")}</span>
                  </Space>
                </Divider>
                {loadingSupportDocuments[contract.id] ? (
                  <Spin />
                ) : contractSupportDocuments[contract.id]?.length > 0 ? (
                  <Table
                    dataSource={contractSupportDocuments[contract.id]}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    columns={[
                      {
                        title: t("customs.filename"),
                        dataIndex: "filename",
                        key: "filename",
                      },
                      {
                        title: t("customs.fileType"),
                        dataIndex: "content_type",
                        key: "content_type",
                      },
                      {
                        title: t("customs.fileSize"),
                        dataIndex: "byte_size",
                        key: "byte_size",
                        render: (size: number) => {
                          if (size < 1024) return `${size} B`;
                          if (size < 1024 * 1024)
                            return `${(size / 1024).toFixed(2)} KB`;
                          return `${(size / (1024 * 1024)).toFixed(2)} MB`;
                        },
                      },
                      {
                        title: t("customs.dateUploaded"),
                        dataIndex: "created_at",
                        key: "created_at",
                        render: (date: string) => {
                          if (!date) return "-";
                          return new Date(date).toLocaleDateString();
                        },
                      },
                      {
                        title: t("common.actions"),
                        key: "action",
                        render: (
                          _: unknown,
                          doc: LeanCustomsContractSupportDocument
                        ) => (
                          <Button
                            type="link"
                            size="small"
                            icon={<DownloadOutlined />}
                            onClick={() =>
                              onDownloadSupportDocument(
                                contract.id,
                                doc.id,
                                doc.filename
                              )
                            }
                          >
                            {t("customs.download")}
                          </Button>
                        ),
                      },
                    ]}
                  />
                ) : (
                  <Empty
                    description={t("customs.noDocuments")}
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    style={{ padding: "20px 0" }}
                  />
                )}
              </div>
            </Panel>
          ))}
        </Collapse>
      ) : (
        <Empty
          description={t("customs.noContracts")}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}
    </>
  );
};
